import { Table, Badge, Group, Text, ActionIcon, Skeleton, Code } from '@mantine/core'
import { IconTrash, IconTags } from '@tabler/icons-react'
import type { Volume } from '@/types'
import { formatDistanceToNow } from 'date-fns'

interface VolumeTableProps {
  volumes: Volume[]
  loading?: boolean
  onDelete?: (name: string) => void
}

export function VolumeTable({ volumes, loading, onDelete }: VolumeTableProps) {
  if (loading) {
    return (
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Driver</Table.Th>
            <Table.Th>Scope</Table.Th>
            <Table.Th>Mountpoint</Table.Th>
            <Table.Th>Usage</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {[...Array(5)].map((_, i) => (
            <Table.Tr key={i}>
              <Table.Td><Skeleton height={20} width={120} /></Table.Td>
              <Table.Td><Skeleton height={20} width={80} /></Table.Td>
              <Table.Td><Skeleton height={20} width={60} /></Table.Td>
              <Table.Td><Skeleton height={20} width={200} /></Table.Td>
              <Table.Td><Skeleton height={20} width={100} /></Table.Td>
              <Table.Td><Skeleton height={20} width={100} /></Table.Td>
              <Table.Td><Skeleton height={20} width={40} /></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  if (volumes.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        No volumes found. Create one to get started.
      </Text>
    )
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
  }

  const rows = volumes.map((volume) => {
    const hasLabels = Object.keys(volume.Labels).length > 0
    const refCount = volume.UsageData?.RefCount || 0

    return (
      <Table.Tr key={volume.Name}>
        <Table.Td>
          <Group gap="xs">
            <Text fw={500}>{volume.Name}</Text>
            {hasLabels && (
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                title={Object.entries(volume.Labels).map(([k, v]) => `${k}=${v}`).join(', ')}
              >
                <IconTags size={14} />
              </ActionIcon>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Badge variant="light" color="blue" size="sm">
            {volume.Driver}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge variant="outline" size="sm">
            {volume.Scope}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Code style={{ fontSize: '0.75rem' }}>{volume.Mountpoint}</Code>
        </Table.Td>
        <Table.Td>
          {volume.UsageData ? (
            <Group gap="xs">
              <Text size="sm">
                {formatBytes(volume.UsageData.Size)}
              </Text>
              <Badge
                size="sm"
                color={refCount > 0 ? 'green' : 'gray'}
                variant="dot"
              >
                {refCount} {refCount === 1 ? 'container' : 'containers'}
              </Badge>
            </Group>
          ) : (
            <Text size="sm" c="dimmed">-</Text>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm" c="dimmed">
            {formatDistanceToNow(new Date(volume.CreatedAt), { addSuffix: true })}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap="xs" justify="flex-end">
            <ActionIcon
              variant="subtle"
              color="red"
              title="Delete volume"
              onClick={() => onDelete?.(volume.Name)}
              disabled={refCount > 0}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <Table horizontalSpacing="md" verticalSpacing="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Driver</Table.Th>
          <Table.Th>Scope</Table.Th>
          <Table.Th>Mountpoint</Table.Th>
          <Table.Th>Usage</Table.Th>
          <Table.Th>Created</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  )
}
